/**
 * Drawing a relationship: the bar that appears once two things are picked.
 *
 * Contextual rather than always-on. A genogram has twenty-five relationship
 * types; shown at once they are a wall, and shown beside a board they are a
 * wall taking up a third of it. Two elements are picked, and the bar offers
 * the three families the notation has — couple, children, emotional bond —
 * with the options one press away.
 *
 * A family the notation does not allow between these two is shown disabled
 * with the reason, never hidden: a palette that silently omits what it will
 * not do teaches nothing, and the reason is where a therapist new to the
 * notation learns that children hang off the couple's line.
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { ArrowLeft, ArrowLeftRight, ArrowRight, ChevronUp, Link2 } from "lucide-react";

/** Which way each tie is drawn, as the mark it is drawn with. */
const ENERGY_ARROW = {
    towardPerson: ArrowLeft,
    towardSystem: ArrowRight,
    both: ArrowLeftRight,
} as const;

import { ecomapLinkOptions } from "../../domain/ecomapRules";
import { genogramLinkOptions, type Selectable } from "../../domain/genogramRules";
import type { MapLink } from "../../domain/types";
import {
    CHILD_TYPES,
    EMOTIONAL_TYPES,
    ENERGY_DIRECTIONS,
    UNION_TYPES,
    type ChildType,
    type EmotionalType,
    type EnergyDirection,
    type LinkStrength,
    type MapKind,
    type UnionType,
} from "../../domain/types";

interface Props {
    kind: MapKind;
    first: Selectable;
    second: Selectable;
    /** What else is on the board, for the rules that depend on it. */
    links: MapLink[];
    onCreateUnion: (union: UnionType) => void;
    onCreateChild: (child: ChildType) => void;
    onCreateEmotional: (emotional: EmotionalType) => void;
    onCreateSystem: (strength: LinkStrength, energy: EnergyDirection) => void;
}

export default function LinkPalette({
    kind,
    first,
    second,
    links,
    onCreateUnion,
    onCreateChild,
    onCreateEmotional,
    onCreateSystem,
}: Props) {
    const { t } = useTranslation();
    const [openFamily, setOpenFamily] = useState<string | null>(null);

    const options =
        kind === "genogram"
            ? genogramLinkOptions(first, second, { links })
            : ecomapLinkOptions(first, second, { links });
    const allowed = new Map(options.map((option) => [option.type, option]));

    const status = (type: string) => {
        const option = allowed.get(type as never);
        return {
            enabled: Boolean(option?.enabled),
            reason: option?.reasonKey ? t(option.reasonKey) : undefined,
        };
    };

    /**
     * Only what these two elements can actually be joined with.
     *
     * The bar used to show every family greyed out with its reason, on the
     * grounds that a palette which hides what it will not do teaches nothing.
     * In a session it read as a wall of dead controls: a person and a system
     * have exactly one thing between them, and the other three were noise.
     * The reason is still said — when nothing at all fits, which is when a
     * therapist actually needs it.
     */
    const offered = options.filter((option) => option.enabled);
    const nothingFits = offered.length === 0;
    const why = options.find((option) => option.reasonKey)?.reasonKey;
    const shows = (type: string) => offered.some((option) => option.type === type);

    return (
        <div
            role="toolbar"
            aria-label={t("systemicMap.palette.connect")}
            className="flex items-center gap-1 rounded-xl border border-border-subtle bg-surface-raised p-1.5 shadow-theme-lg"
        >
            {nothingFits && why ? (
                <span className="px-2 py-1 text-xs font-medium text-content-secondary">
                    {t(why)}
                </span>
            ) : (
                // A mark rather than a sentence. "Choose the relationship
                // between the two selected elements" wrapped to five lines
                // and took as much of the bar as the choices did — and the
                // choices already say it.
                <span
                    aria-hidden="true"
                    title={t("systemicMap.palette.connectReady")}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-content-tertiary"
                >
                    <Link2 size={16} />
                </span>
            )}

            <>
                    {shows("union") && (
                    <Family
                        id="union"
                        title={t("systemicMap.palette.union")}
                        values={UNION_TYPES}
                        translate={(value) => t(`systemicMap.union.${value}`)}
                        {...status("union")}
                        open={openFamily === "union"}
                        onToggle={setOpenFamily}
                        onPick={(value) => onCreateUnion(value as UnionType)}
                    />
                    )}
                    {shows("child") && (
                    <Family
                        id="child"
                        title={t("systemicMap.palette.child")}
                        values={CHILD_TYPES}
                        translate={(value) => t(`systemicMap.child.${value}`)}
                        {...status("child")}
                        open={openFamily === "child"}
                        onToggle={setOpenFamily}
                        onPick={(value) => onCreateChild(value as ChildType)}
                    />
                    )}
                    {shows("emotional") && (
                    <Family
                        id="emotional"
                        title={t("systemicMap.palette.emotional")}
                        values={EMOTIONAL_TYPES}
                        translate={(value) => t(`systemicMap.emotional.${value}`)}
                        {...status("emotional")}
                        open={openFamily === "emotional"}
                        onToggle={setOpenFamily}
                        onPick={(value) => onCreateEmotional(value as EmotionalType)}
                    />
                    )}
            </>
            {/* The tie is what belongs to this instrument alone; the rest of
                the bar is the household inside the circle. */}
            {kind === "ecomap" && shows("system") && (
                <SystemTieBuilder {...status("system")} onCreate={onCreateSystem} />
            )}
        </div>
    );
}

/**
 * One family of relationships: a button, and its options above it.
 *
 * The options open upward because the bar sits at the foot of the board — a
 * menu opening downward would fall off the screen.
 */
function Family({
    id,
    title,
    values,
    translate,
    enabled,
    reason,
    open,
    onToggle,
    onPick,
}: {
    id: string;
    title: string;
    values: readonly string[];
    translate: (value: string) => string;
    enabled: boolean;
    reason?: string;
    open: boolean;
    onToggle: (id: string | null) => void;
    onPick: (value: string) => void;
}) {
    const wrapper = useRef<HTMLDivElement | null>(null);

    useEffect(() => {
        if (!open) return;
        const away = (event: MouseEvent) => {
            if (!wrapper.current?.contains(event.target as Node)) onToggle(null);
        };
        const escape = (event: KeyboardEvent) => {
            if (event.key === "Escape") onToggle(null);
        };
        document.addEventListener("mousedown", away);
        document.addEventListener("keydown", escape);
        return () => {
            document.removeEventListener("mousedown", away);
            document.removeEventListener("keydown", escape);
        };
    }, [open, onToggle]);

    return (
        <div ref={wrapper} className="relative">
            <button
                type="button"
                disabled={!enabled}
                aria-expanded={open}
                // The reason is the tooltip and the title both: a disabled
                // control whose explanation only exists on hover explains
                // nothing to a keyboard.
                title={enabled ? title : reason}
                onClick={() => onToggle(open ? null : id)}
                className="flex min-h-11 items-center gap-1.5 rounded-lg border border-border-strong px-3 text-sm font-medium text-content-primary transition-colors duration-fast hover:bg-surface-sunken disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
            >
                {title}
                {/* These open a list; without the mark they read as buttons
                    that do something on their own. */}
                <ChevronUp
                    size={14}
                    aria-hidden="true"
                    className={`text-content-tertiary transition-transform duration-fast ${
                        open ? "rotate-180" : ""
                    }`}
                />
            </button>

            {!enabled && reason && (
                <span className="sr-only" role="note">
                    {reason}
                </span>
            )}

            {open && enabled && (
                <div className="absolute bottom-full left-1/2 z-10 mb-2 w-56 -translate-x-1/2 rounded-xl border border-border-subtle bg-surface-overlay p-1.5 shadow-theme-lg">
                    {values.map((value) => (
                        <button
                            key={value}
                            type="button"
                            onClick={() => {
                                onToggle(null);
                                onPick(value);
                            }}
                            className="flex min-h-11 w-full items-center rounded-lg px-3 text-left text-sm text-content-primary transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                        >
                            {translate(value)}
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

/**
 * An ecomap tie is two choices, not one.
 *
 * Strength and direction are independent axes in Hartman's notation — a strong
 * tie can run either way, a stressful one can be mutual — so they are picked
 * separately rather than flattened into nine buttons.
 */
/**
 * The tie itself: which way the energy runs.
 *
 * Strength used to be chosen here too, and it made the bar say two things at
 * once — picking "strong" looked like it drew nothing, because it only set
 * what the next press would draw. The tie is created at its ordinary strength
 * and changed afterwards by selecting it, which is where every other property
 * of a relationship is changed.
 */
function SystemTieBuilder({
    enabled,
    reason,
    onCreate,
}: {
    enabled: boolean;
    reason?: string;
    onCreate: (strength: LinkStrength, energy: EnergyDirection) => void;
}) {
    const { t } = useTranslation();

    if (!enabled) {
        return (
            <span className="px-2 text-xs text-content-secondary" role="note">
                {reason}
            </span>
        );
    }

    return (
        <span className="flex items-center gap-1">
            {ENERGY_DIRECTIONS.map((energy) => {
                const Arrow = ENERGY_ARROW[energy];
                return (
                    <button
                        key={energy}
                        type="button"
                        onClick={() => onCreate("strong", energy)}
                        // The arrow is the notation; the words are the gloss.
                        // Three sentences side by side made the bar wider than
                        // the board could hold.
                        title={t(`systemicMap.energy.${energy}`)}
                        className="flex min-h-11 items-center gap-1.5 whitespace-nowrap rounded-lg border border-border-strong px-3 text-sm font-medium text-content-primary transition-colors duration-fast hover:bg-surface-sunken focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                    >
                        <Arrow size={16} aria-hidden="true" />
                        {t(`systemicMap.energyShort.${energy}`)}
                    </button>
                );
            })}
        </span>
    );
}
